import { MessageCircle, Send } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { websocketService } from '../../services/websocket';
import './ProjectMessages.css';

import { API_URL } from '../../config/api';
interface Message { id: number; user_id: number | null; agent_id: number | null; content: string; created_at: string; user_name?: string; agent_name?: string }
interface ProjectMessagesProps { projectId: number }

export default function ProjectMessages({ projectId }: ProjectMessagesProps) {
  const [messages, setMessages] = useState<Message[]>([])
  const [loading, setLoading] = useState(true)
  const [newMessage, setNewMessage] = useState('')
  const messagesEndRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const loadMessages = async () => {
      try {
        const res = await fetch(`${API_URL}/api/projects/${projectId}/messages`)
        const data = await res.json()
        setMessages((data.messages || []) as Message[])
      } catch (err) {
        console.error('Messages load error:', err)
      } finally {
        setLoading(false)
      }
    }
    void loadMessages()

    // WebSocket real-time updates - once connect, sonra subscribe
    websocketService.connect(0).then(() => {
      websocketService.subscribe('projects')
    }).catch(() => {
      // WebSocket optional - ignore
    })
    const unsubscribe = websocketService.on('project_update', (event) => {
      interface ProjectEventData {
        project_id: number;
        event_type: string;
        data: Message;
      }
      const data = event.data as ProjectEventData | undefined
      if (data && data.project_id === projectId && data.event_type === 'message_sent') {
        setMessages(prev => [...prev, data.data])
      }
    })

    return () => {
      unsubscribe()
    }
  }, [projectId])

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }) }, [messages])

  const handleSend = () => {
    if (!newMessage.trim()) {return}
    setNewMessage('')
  }

  const getAgentGradient = (index: number) => {
    const gradients = ['linear-gradient(135deg, #667eea, #764ba2)', 'linear-gradient(135deg, #10b981, #059669)', 'linear-gradient(135deg, #ec4899, #db2777)']
    return gradients[index % gradients.length]
  }

  if (loading) {return <div style={{ padding: '20px', textAlign: 'center' }}>Loading...</div>}

  return (
    <div className="messages-container">
      <div className="messages-list">
        {messages.length === 0 ? (
          <div className="messages-empty"><MessageCircle size={64} /><h3>No messages yet</h3><p>Start the conversation</p></div>
        ) : (
          messages.map((msg, index) => {
            const name = msg.agent_name || msg.user_name || 'Unknown'
            const initial = name.charAt(0).toUpperCase()
            return (
              <div key={msg.id} className="message-item">
                <div className="message-avatar" style={{ background: getAgentGradient(index) }}>{initial}</div>
                <div className="message-content">
                  <div className="message-header">
                    <span className="message-name">{name}</span>
                    <span className="message-time">{new Date(msg.created_at).toLocaleTimeString()}</span>
                    {msg.agent_id && <div className="message-dot" />}
                  </div>
                  <div className="message-text">{msg.content}</div>
                </div>
              </div>
            )
          })
        )}
        <div ref={messagesEndRef} />
      </div>
      <div className="messages-input">
        <div className="input-box">
          <MessageCircle size={18} style={{ color: '#94a3b8' }} />
          <input type="text" placeholder="Type a message..." value={newMessage} onChange={(e) => setNewMessage(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { handleSend() } }} />
          <button className="send-btn" onClick={() => { handleSend() }} disabled={!newMessage.trim()}><Send size={14} /> Send</button>
        </div>
      </div>
    </div>
  )
}
